import {
  FloatingNextArrow,
  FloatingPrevArrow,
  PlayPauseToggle,
} from '@/components/CarouselControls';
import { useEffect, useMemo, useRef } from 'react';
import {
  Carousel,
  CarouselItem,
  CarouselScroller,
  CarouselTab,
  CarouselTabs,
} from 'react-aria-carousel';
import type { HomePageHeroBannerData, HeroSlide } from './types';

const CtaChevron = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const Slide = ({ slide, index }: { slide: HeroSlide; index: number }) => {
  const textStyle = slide.textColor ? { color: slide.textColor } : undefined;

  // Stable object identity. React diffs dangerouslySetInnerHTML by reference, so a
  // fresh literal re-assigns innerHTML on every commit and replaces the text nodes
  // inside the carousel, which trips react-aria-carousel's MutationObserver — it
  // calls hasAttribute() on every added/removed node, and Text nodes have none.
  const headlineHtml = useMemo(
    () => (slide.headline ? { __html: slide.headline } : undefined),
    [slide.headline],
  );

  const showButton = slide.showCtaButton !== false;
  const hasLink = !!slide.ctaUrl;
  const isSlideClickable = !showButton && hasLink;
  const hasVideo = !!slide.backgroundVideo;
  const hasImage = !!slide.backgroundImage;
  const alt = slide.backgroundImageAltText || '';

  return (
    <div className="nfp12-frame" data-ctacolor={slide.ctaColor || 'white'}>
      <div className="nfp12-bg">
        {hasVideo ? (
          <video
            src={slide.backgroundVideo!}
            poster={slide.poster || slide.backgroundImage || undefined}
            autoPlay
            loop
            muted
            playsInline
          />
        ) : hasImage ? (
          <picture>
            {slide.mobileImage ? (
              <source media="(max-width: 48rem)" srcSet={slide.mobileImage} />
            ) : null}
            <img
              src={slide.backgroundImage!}
              alt={alt}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : undefined}
              decoding={index === 0 ? undefined : 'async'}
            />
          </picture>
        ) : null}
      </div>

      {isSlideClickable ? (
        <a className="nfp12-slide-link" href={slide.ctaUrl!} aria-label={slide.ctaText || slide.headline || 'View'} />
      ) : null}

      <div className="nfp12-slide is-active">
        <div className="nfp12-copy">
          {headlineHtml ? (
            <h2
              className="nfp12-title"
              style={textStyle}
              dangerouslySetInnerHTML={headlineHtml}
            />
          ) : null}
          {slide.subHeadline ? (
            <p className="nfp12-sub" style={textStyle}>{slide.subHeadline}</p>
          ) : null}
          {showButton && hasLink ? (
            <a className="nfp12-cta" href={slide.ctaUrl!}>
              {slide.ctaText || 'Shop now'}
              {CtaChevron}
            </a>
          ) : null}
        </div>

      </div>
    </div>
  );
};

export const HomePageHeroBanner = ({
  data,
}: {
  data: HomePageHeroBannerData;
}) => {
  const slides = data.slides ?? [];
  const mountRef = useRef<HTMLElement>(null);

  // Remove the server-rendered placeholder image once React has mounted the
  // real carousel. The placeholder is an aria-hidden sibling rendered by Razor
  // so the browser can paint the LCP image before JS loads.
  useEffect(() => {
    const host = mountRef.current?.closest('[data-react-component]');
    if (!host) return;
    const placeholder = host.querySelector(':scope > .nfp12[aria-hidden]');
    placeholder?.remove();
  }, []);

  if (slides.length === 0) {
    return null;
  }

  const isSingle = slides.length === 1;

  return (
    <section ref={mountRef} className={`nfp12${data.fullWidth ? ' nfp12--full-width' : ''}`}>
      <Carousel
        className="nfp12-carousel"
        aria-label="Homepage hero banner"
        autoplay={data.autoPlay && !isSingle}
        autoplayInterval={5000}
        itemsPerPage={1}
        loop="infinite"
      >
        <CarouselScroller className="nfp12-scroller">
          {slides.map((slide, index) => (
            <CarouselItem key={`${data.key}-${index}`}>
              <Slide slide={slide} index={index} />
            </CarouselItem>
          ))}
        </CarouselScroller>

        {!isSingle ? (
          <>
            <FloatingPrevArrow />
            <FloatingNextArrow />
            <div className="nfp12-controls">
              <CarouselTabs className="nfp12-dots">
                {({ index }) => (
                  <CarouselTab index={index} className="nfp12-dot" />
                )}
              </CarouselTabs>
              {data.autoPlay ? <PlayPauseToggle /> : null}
            </div>
          </>
        ) : null}
      </Carousel>
    </section>
  );
};
